import type { ApiErrorBody, ApiErrorCode } from "@/lib/api/types"

export type ValidationErrors = Record<string, string[]>

type ApiErrorOptions = {
  status: number
  message: string
  code?: ApiErrorCode
  errors?: ValidationErrors
}

export class ApiError extends Error {
  readonly status: number
  readonly code?: ApiErrorCode
  readonly errors?: ValidationErrors

  constructor({ status, message, code, errors }: ApiErrorOptions) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.code = code
    this.errors = errors
  }

  static fromBody(status: number, body: unknown): ApiError {
    const failure = (
      typeof body === "object" && body !== null ? body : {}
    ) as Partial<ApiErrorBody>

    return new ApiError({
      status,
      message: failure.message ?? "Request failed",
      code: failure.code,
      errors: failure.errors,
    })
  }

  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isValidation(): boolean {
    return this.status === 422
  }

  get isOrganisationAccessDenied(): boolean {
    return this.code === "tenant_required" || this.code === "tenant_unavailable"
  }

  get isClientFault(): boolean {
    return (
      this.status >= 400 &&
      this.status < 500 &&
      this.status !== 408 &&
      this.status !== 429
    )
  }

  get isServerFault(): boolean {
    return this.status >= 500
  }

  firstError(field: string): string | undefined {
    return this.errors?.[field]?.[0]
  }
}

export function fieldError(error: unknown, field: string): string | undefined {
  return isApiError(error) && error.isValidation
    ? error.firstError(field)
    : undefined
}

export class NetworkError extends Error {
  constructor(message = "Could not reach the server. Check your connection.") {
    super(message)
    this.name = "NetworkError"
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError
}

export function toErrorMessage(error: unknown): string {
  if (isApiError(error) && error.isServerFault) {
    return "Something went wrong on our side. Please try again."
  }

  if (isApiError(error) || isNetworkError(error)) {
    return error.message
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return "Something went wrong. Please try again."
}
